import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { Button, FileUpload, FormRow, TagsInput } from '../controls';

const defaultLinks = {
  instagram: '',
  twitter: '',
  facebook: '',
  web: '',
};

function buildDefaultValues(performer) {
  return {
    performerDescription: performer?.performerDescription || '',
    performerName: performer?.performerName || '',
    hometown: performer?.hometown || '',
    type: performer?.type || undefined,
    subtype: performer?.subtype || undefined,
    links: performer?.links || defaultLinks,
  };
}

export function PerformerForm({
  performer,
  handleSave,
  performerTypes,
  onFileUpload,
  onFileDelete,
  formRef = undefined,
  className = undefined,
  isPending = false,
}) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: buildDefaultValues(performer),
    mode: 'onSubmit',
  });

  const performerId = performer?.performerId;

  useEffect(() => {
    reset(buildDefaultValues(performer));
  }, [performerId, reset]);

  const types = performerTypes.types || [];
  const subtypes = performerTypes.subtypes || [];

  const fileList = [];

  if (performer?.photoURL) {
    fileList.push({
      uid: '-1',
      name: performer.performerPhoto,
      url: performer.photoURL,
      status: 'done',
    });
  }

  return (
    <form
      ref={formRef}
      className={cn(className)}
      onSubmit={handleSubmit(handleSave)}
      noValidate
    >
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="min-w-0 flex-1">
          <FormRow label="Performer Name" error={errors.performerName?.message}>
            <input
              type="text"
              placeholder="Name"
              {...register('performerName', {
                required: 'Performer name is required',
              })}
            />
          </FormRow>
          <FormRow label="Hometown">
            <input
              type="text"
              placeholder="Hometown"
              {...register('hometown')}
            />
          </FormRow>
          <FormRow label="Type" error={errors.type?.message}>
            <Controller
              name="type"
              control={control}
              rules={{ required: 'Type is required' }}
              render={({ field }) => (
                <TagsInput
                  value={field.value}
                  placeholder="Select or enter a type"
                  options={types}
                  onChange={field.onChange}
                />
              )}
            />
          </FormRow>
          <FormRow label="Subtype">
            <Controller
              name="subtype"
              control={control}
              render={({ field }) => (
                <TagsInput
                  value={field.value}
                  placeholder="Select or enter a subtype"
                  options={subtypes}
                  onChange={field.onChange}
                />
              )}
            />
          </FormRow>
        </div>
        <div className="w-full shrink-0 sm:w-48 md:w-56">
          <FormRow>
            <div className="w-full">
              <FileUpload
                fileList={fileList}
                onFileUpload={onFileUpload}
                onFileDelete={onFileDelete}
              />
            </div>
          </FormRow>
        </div>
      </div>
      <FormRow label="Notes">
        <textarea
          placeholder="Performer Biography..."
          {...register('performerDescription')}
        />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fas', 'globe']} />}>
        <input type="text" placeholder="Website" {...register('links.web')} />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fab', 'facebook']} />}>
        <input
          type="text"
          placeholder="Facebook"
          {...register('links.facebook')}
        />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fab', 'twitter']} />}>
        <input
          type="text"
          placeholder="Twitter"
          {...register('links.twitter')}
        />
      </FormRow>
      <FormRow label={<FontAwesomeIcon icon={['fab', 'instagram']} />}>
        <input
          type="text"
          placeholder="Instagram"
          {...register('links.instagram')}
        />
      </FormRow>
      <div className="mt-8 flex justify-end border-t border-black/[0.08] pt-6 pb-2">
        <Button type="submit" mode="dark" disabled={isPending}>
          Save Performer
        </Button>
      </div>
    </form>
  );
}
